/**
 * A Stimulus controller that enhances Trix editors with advanced features:
 * - Color picker for text styling
 * - Auto-linking of URLs
 * - @mentions with Tribute.js
 * - Keyboard shortcuts
 *
 * @module TrixEnhancedController
 *
 * @example
 * HTML structure:
 * <%= f.input :content,
 *     as: :rich_text_area,
 *     color_picker: true,
 *     auto_linking: true,
 *     mentions: true,
 *     company_id: @company.id %>
 */
import { Controller } from '@hotwired/stimulus';
import Trix from 'trix';
import Tribute from 'tributejs';
import debounce from '../packs/shared/debounce';

const AVAILABLE_COLORS = ['green', 'blue', 'red', 'orange', 'purple'];
const ENTER_KEY_CODE = 13;

const normalizeColor = (color) => {
  if (!color) return null;
  const el = document.createElement('span');
  el.style.color = color;
  document.body.appendChild(el);
  const computed = window.getComputedStyle(el).color;
  document.body.removeChild(el);

  const match = computed.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (match) {
    const [, red, green, blue] = match.map(Number);
    const colorMap = {
      '0,128,0': 'green',
      '0,0,255': 'blue',
      '255,0,0': 'red',
      '255,165,0': 'orange',
      '128,0,128': 'purple'
    };
    return colorMap[`${red},${green},${blue}`] || null;
  }
  return color.toLowerCase();
};

const colorMatches = (styleColor, colorName) => (
  normalizeColor(styleColor) === colorName.toLowerCase()
);

// Helper: Validate URL
const isValidURL = (urlString) => {
  try {
    const url = new URL(urlString);
    return Boolean(url);
  } catch {
    return false;
  }
};

// Helper: Validate and find URLs in text
const getURLsWithRanges = (string) => {
  const pattern = /(\b(?:https?|ftp|file):\/\/[-A-Z0-9+&@#/%?=~_|!:,.;]*)/gi;
  const results = [];

  for (const match of string.matchAll(pattern)) {
    if (isValidURL(match[1])) {
      results.push({ url: match[1], range: [match.index, match.index + match[1].length] });
    }
  }
  return results;
};

export default class extends Controller {
  static values = {
    colorPicker: { type: Boolean, default: false },
    autoLinking: { type: Boolean, default: false },
    mentions: { type: Boolean, default: false },
    companyId: { type: Number, default: null },
    submitButtonSelector: { type: String, default: null },
    inputId: { type: String, default: null }
  }

  static targets = ['colorDialog', 'trixEditor']

  initialize() {
    AVAILABLE_COLORS.forEach((color) => {
      if (!Trix.config.textAttributes[color]) {
        Trix.config.textAttributes[color] = {
          tagName: 'span',
          style: { color: color },
          parser: (element) => colorMatches(element.style.color, color),
          inheritable: true
        };
      }
    });
  }

  connect() {
    this.trixElement = this.element.querySelector('trix-editor');
    if (!this.trixElement) { return }

    if (this.inputIdValue) {
      const inputField = this.element.querySelector(`#${this.inputIdValue}`);
      this.trixElement.setAttribute('input', this.inputIdValue);
      this.trixElement.addEventListener('trix-initialize', () => {
        this.editor = this.trixElement.editor;

        if (inputField && inputField.value) { this.loadHTMLWithColors(inputField.value) }

        this.setupFeatures();
      }, { once: true });
    } else {
      this.editor = this.trixElement.editor;
      this.colorDialogVisible = false;

      if (this.editor) {
        this.setupFeatures();
      } else {
        this.trixElement.addEventListener('trix-initialize', () => {
          this.editor = this.trixElement.editor;
          this.setupFeatures();
        }, { once: true });
      }
    }
  }

  disconnect() {
    if (!this.trixElement) return;

    if (this.autoLinkingValue) {
      this.trixElement.removeEventListener('trix-render', this.autoLinkBound);
    }

    if (this.submitButtonSelectorValue) {
      this.trixElement.removeEventListener('keydown', this.handleKeyboardShortcutBound);
    }

    if (this.mentionsValue && this.tribute) {
      this.tribute.detach(this.trixElement);
      ['tribute-replaced', 'trix-attachment-add', 'trix-attachment-remove'].forEach((event, i) => {
        const handlers = [this.handleTributeReplacedBound, this.handleMentionAddBound, this.handleMentionRemoveBound];
        this.trixElement.removeEventListener(event, handlers[i]);
      });
    }

    if (this.colorDialogVisible) this.hideColorDialog();
  }

  loadHTMLWithColors(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const colorSpans = this.findColorSpans(doc);
    const colorRanges = this.calculateColorRanges(doc, colorSpans);

    this.unwrapColorSpans(colorSpans);
    this.editor.loadHTML(doc.body.innerHTML);
    this.applyColorRanges(colorRanges);
  }

  findColorSpans(doc) {
    return Array.from(doc.querySelectorAll('span[style]'))
      .map((span) => {
        const colorName = AVAILABLE_COLORS.find((name) => (
          colorMatches(span.style.color, name)
        ));
        return colorName ? { element: span, color: colorName } : null;
      })
      .filter(Boolean);
  }

  calculateColorRanges(doc, colorSpans) {
    const colorRanges = [];
    const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
    let textPosition = 0;

    for (let node = walker.nextNode(); node; node = walker.nextNode()) {
      const textLength = node.textContent.length;
      const currentPosition = textPosition;

      colorSpans.forEach(({ element, color }) => {
        if (!element.contains(node)) return;

        let range = colorRanges.find((colorRange) => colorRange.element === element);
        if (!range) {
          range = {
            element,
            color,
            start: currentPosition,
            end: currentPosition + textLength
          };
          colorRanges.push(range);
        } else {
          range.end = currentPosition + textLength;
        }
      });
      textPosition += textLength;
    }
    return colorRanges;
  }

  unwrapColorSpans(colorSpans) {
    colorSpans.forEach(({ element }) => {
      const parent = element.parentNode;
      if (!parent) return;

      while (element.firstChild) { parent.insertBefore(element.firstChild, element) }
      parent.removeChild(element);
    });
  }

  applyColorRanges(colorRanges) {
    colorRanges.forEach(({ color, start, end }) => {
      this.editor.setSelectedRange([start, end]);
      this.editor.activateAttribute(color);
    });

    const docLength = this.editor.getDocument().getLength();
    this.editor.setSelectedRange([docLength, docLength]);
  }

  setupFeatures() {
    if (this.colorPickerValue) { this.setupColorPicker() }
    if (this.autoLinkingValue) { this.setupAutoLinking() }
    if (this.mentionsValue && this.companyIdValue) { this.setupMentions() }
    if (this.submitButtonSelectorValue) { this.setupKeyboardShortcuts() }
  }

  setupColorPicker() {
    this.addColorPickerButton();
  }

  addColorPickerButton() {
    setTimeout(() => {
      const toolbar = this.element.querySelector('trix-toolbar');
      if (!toolbar || toolbar.querySelector('.trix-button--icon-font-color')) return;

      const buttonGroup = toolbar.querySelector('[data-trix-button-group="text-tools"]')
        || toolbar.querySelector('.trix-button-group');
      if (!buttonGroup) return;

      const colorButton = Object.assign(document.createElement('button'), {
        type: 'button',
        className: 'trix-button trix-button--icon trix-button--icon-font-color',
        tabIndex: -1,
        title: 'Text Color'
      });
      colorButton.setAttribute('data-action', 'click->trix-enhanced#toggleColorDialog');
      buttonGroup.appendChild(colorButton);
    }, 0);
  }

  toggleColorDialog(event) {
    event.preventDefault();
    this.colorDialogVisible = !this.colorDialogVisible;

    if (this.hasColorDialogTarget) {
      if (this.colorDialogVisible) {
        const button = event.currentTarget;
        const buttonRect = button.getBoundingClientRect();
        const containerRect = this.element.getBoundingClientRect();
        const top = buttonRect.bottom - containerRect.top;
        const left = buttonRect.left - containerRect.left;

        this.colorDialogTarget.style.display = 'flex';
        this.colorDialogTarget.style.top = `${top}px`;
        this.colorDialogTarget.style.left = `${left}px`;
      } else {
        this.colorDialogTarget.style.display = 'none';
      }
    }
  }

  hideColorDialog() {
    this.colorDialogVisible = false;
    if (this.hasColorDialogTarget) { this.colorDialogTarget.style.display = 'none' }
  }

  // Apply color to selected text
  applyColor(event) {
    event.preventDefault();
    const selectedColor = event.currentTarget.dataset.color;
    if (!selectedColor || !this.editor) { return }

    if (selectedColor === 'black') {
      AVAILABLE_COLORS.forEach((color) => { this.editor.deactivateAttribute(color) });
    } else {
      AVAILABLE_COLORS.forEach((color) => {
        if (color !== selectedColor) { this.editor.deactivateAttribute(color) }
      });

      const isActive = this.editor.attributeIsActive(selectedColor);
      if (isActive) {
        this.editor.deactivateAttribute(selectedColor);
      } else {
        this.editor.activateAttribute(selectedColor);
      }
    }
    this.hideColorDialog();
    this.trixElement.focus();
  }

  setupAutoLinking() {
    this.autoLinkBound = this.autoLink.bind(this);
    this.trixElement.addEventListener('trix-render', this.autoLinkBound);
    this.autoLink();
  }

  autoLink() {
    setTimeout(() => {
      if (!this.editor) return;

      const currentRange = this.editor.getSelectedRange();
      const text = this.editor.getDocument().toString();

      getURLsWithRanges(text).forEach(({ url, range }) => {
        const existingHref = this.editor.getDocument().getCommonAttributesAtRange(range).href;

        if (existingHref !== url) {
          this.editor.setSelectedRange(range);
          if (this.editor.canActivateAttribute('href')) { this.editor.activateAttribute('href', url) }
        }
      });
      this.editor.setSelectedRange(currentRange);
    }, 0);
  }

  setupMentions() {
    this.mentionsXhr = null;

    const fetchUsers = (text, callback) => {
      if (this.mentionsXhr) { this.mentionsXhr.abort() }

      const xhr = window.serverClient.api.traxCompanies.mentions.getUsers({ traxCompanyId: this.companyIdValue }, { query: text });
      this.mentionsXhr = xhr;

      xhr.then((users) => {
        callback(users.users);
      }).catch((error, status) => {
        if (status !== 'abort') { callback([]) }
      });
    };

    const pasteHtml = (html, startPos, endPos) => {
      const position = this.editor.getPosition();
      this.editor.setSelectedRange([position - (endPos - startPos), position]);
      this.editor.deleteInDirection('backward');
    };

    this.handleTributeReplacedBound = this.handleTributeReplaced.bind(this);
    this.handleMentionAddBound = this.handleMentionAdd.bind(this);
    this.handleMentionRemoveBound = this.handleMentionRemove.bind(this);

    this.tribute = new Tribute({
      allowSpaces: false,
      lookup: 'full_name',
      searchOpts: { skip: true },
      values: debounce(fetchUsers, 200),
      menuItemLimit: 10
    });

    this.tribute.attach(this.trixElement);
    this.tribute.range.pasteHtml = pasteHtml;

    this.trixElement.addEventListener('tribute-replaced', this.handleTributeReplacedBound);
    this.trixElement.addEventListener('trix-attachment-add', this.handleMentionAddBound);
    this.trixElement.addEventListener('trix-attachment-remove', this.handleMentionRemoveBound);
  }

  handleTributeReplaced(event) {
    if (event.detail.item === undefined) {
      this.editor.insertString(event.detail.context.mentionText);
      return;
    }

    const mention = event.detail.item.original;
    const attachment = new Trix.Attachment({
      userId: mention.id,
      sgid: mention.sgid,
      content: mention.content,
      type: 'mention'
    });

    this.editor.insertAttachment(attachment);
    this.editor.insertString(' ');
  }

  handleMentionAdd(event) {
    if (event.attachment.getAttribute('type') === 'mention') {
      this.element.dispatchEvent(new CustomEvent('trix-mention-added', {
        detail: { userId: event.attachment.getAttribute('userId') },
        bubbles: true
      }));
    }
  }

  handleMentionRemove(event) {
    if (event.attachment.getAttribute('type') === 'mention') {
      this.element.dispatchEvent(new CustomEvent('trix-mention-removed', {
        detail: { userId: event.attachment.getAttribute('userId') },
        bubbles: true
      }));
    }
  }

  setupKeyboardShortcuts() {
    this.handleKeyboardShortcutBound = this.handleKeyboardShortcut.bind(this);
    this.trixElement.addEventListener('keydown', this.handleKeyboardShortcutBound);
  }

  handleKeyboardShortcut(event) {
    if ((event.metaKey || event.ctrlKey) && event.keyCode === ENTER_KEY_CODE) {
      const submitButton = document.querySelector(this.submitButtonSelectorValue);
      if (submitButton) { submitButton.click() }
    }
  }
}
